import { useCallback, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { fetchWatchlistItems } from 'src/services/apiRequests/WatchlistService';
import { commerceCTA } from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { ContentItemType } from 'src/utils/commonTypes';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import { MIXPANEL_SHOP } from '../../utils/analytics/mixpanel/mixpanelConstants';
import { HTTP_HEADER_KEYS } from '../../utils/constants/HttpMethodsConstants';
import { MetaButtonSkeleton } from '../consumptionPage/ConsumptionMetaData/ContentMetaDataSkeletonLoader/style';
import { checkIfContentAvailableInWatchlist } from './WatchlistBtn/WatchlistHandler';
import WatchTrailerBtn from './WatchTrailerBtn/WatchTrailerBtn';

const SkeletonMetaButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
`;

const LikeDislikeBtn = dynamic(
  () => import('./LikeDislikeBtn/LikeDislikeBtn'),
  {
    loading: () => (
      <SkeletonMetaButtonsWrapper>
        <MetaButtonSkeleton />
        <MetaButtonSkeleton />
      </SkeletonMetaButtonsWrapper>
    ),
    ssr: false
  }
);
const ShopBtn = dynamic(() => import('./ShopBtn'), {
  loading: () => <MetaButtonSkeleton />,
  ssr: false
});
const Share = dynamic(() => import('./Share/Share'), {
  loading: () => <MetaButtonSkeleton />,
  ssr: false
});

const WatchListBtn = dynamic(() => import('./WatchlistBtn/WatchlistBtn'), {
  loading: () => <MetaButtonSkeleton />,
  ssr: false
});

interface MetaButtonsProps {
  metaButtonItems: ContentItemType;
  fromCarousel?: boolean;
  getSinglePlayDetails: (spApiObject: any) => any;
  handleCommerceLayout?: () => void;
  singlePlayData?: any;
}

const MetaButtons: React.FC<MetaButtonsProps> = ({
  metaButtonItems,
  getSinglePlayDetails,
  handleCommerceLayout,
  fromCarousel = false,
  singlePlayData
}) => {
  const [isPresentInWatchlist, setIsPresentInWatchlist] = useState(false);

  const {
    remoteConfig,
    isUserLoggedIn,
    setUserWatchlist,
    userWatchlist,
    commerceComponentData
  } = useAppStore();
  const [commerceFeatureFlag, setCommerceFeatureFlag] =
    useState<boolean>(false);
  const commerceDataLength = commerceComponentData?.length || 0;
  const router = useRouter();

  const fetchWatchlist = async () => {
    const headers = [
      HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
      HTTP_HEADER_KEYS.AUTHORIZATION
    ];
    const watchlistResponse = await fetchWatchlistItems(headers);
    setUserWatchlist({ ...watchlistResponse });
  };

  const updateWatchlistSymbol = useCallback(() => {
    setIsPresentInWatchlist((ps) => !ps);
  }, []);

  useEffect(() => {
    if (isUserLoggedIn && !userWatchlist.isWatchlistDataFetched) {
      fetchWatchlist();
    }
    if (
      isUserLoggedIn &&
      checkIfContentAvailableInWatchlist(userWatchlist, metaButtonItems)
    ) {
      setIsPresentInWatchlist(true);
    } else {
      setIsPresentInWatchlist(false);
    }
  }, [router?.asPath, userWatchlist.isWatchlistDataFetched]);

  useEffect(() => {
    const commerce_feature_flag = isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.CONFIG_COMMERCE
    );

    setCommerceFeatureFlag(commerce_feature_flag);
  }, [remoteConfig?.length]);

  const isLikeDislikeEnabled = () => {
    return (
      isFeatureEnabled(
        remoteConfig,
        FeatureFlagConstants.FEATURE_USER_REACTION
      ) || false
    );
  };

  const shopBtnHandler = useCallback(() => {
    commerceCTA(MIXPANEL_SHOP);
    if (handleCommerceLayout) {
      handleCommerceLayout();
    }
  }, []);
  return (
    <>
      {isLikeDislikeEnabled() ? <LikeDislikeBtn item={metaButtonItems} /> : ''}

      <Share
        fromCarousel={fromCarousel}
        item={metaButtonItems}
        isReferralURL={false}
        isSportsLiveEvent={false}
      />
      {commerceFeatureFlag && (
        <ShopBtn
          disable={commerceDataLength === 0 ? true : false}
          handleCommerceLayout={shopBtnHandler}
        />
      )}
      <WatchListBtn
        item={metaButtonItems}
        isPresentInWatchlist={isPresentInWatchlist}
        updateWatchlistSymbol={updateWatchlistSymbol}
        contentPartnerDetails={
          singlePlayData?.assetDetails?.content_partner_details
        }
      />
      {metaButtonItems?.trailerData?.id ? (
        <WatchTrailerBtn
          metaButtonItems={metaButtonItems}
          getSinglePlayDetails={getSinglePlayDetails}
          singlePlayData={singlePlayData}
        />
      ) : (
        metaButtonItems.trailerData === null && <MetaButtonSkeleton />
      )}
    </>
  );
};

export default MetaButtons;
